/**
 * Workspace session.
 *
 * Production: the Logto OIDC redirect returns an Org Token; we keep it in
 * memory + localStorage and attach it as a bearer token. The gateway verifies
 * the JWT against Logto's JWKS, resolves the organization claim to a tenant and
 * only then forwards downstream (ADR-001/002/015).
 *
 * Local development: `VNMADE_ALLOW_DEV_TOKEN=true` lets both the gateway and each
 * service accept a `dev.` token — base64(JSON), unsigned, **local only**. The
 * mint below produces exactly the claim shape `verifyBearerToken` expects.
 */
export const PRESETS = [
    {
        id: "buyer",
        label: "买家 / 采购经理",
        labelEn: "Buyer",
        scopes: ["commerce:*"],
        home: "/workspace",
        blurb: "RFQ、报价比较、订单与结算",
    },
    {
        id: "rnd",
        label: "产品研发",
        labelEn: "Product R&D",
        scopes: ["rnd:*"],
        home: "/rd",
        blurb: "AI 设计任务、工程版本、DFM/BOM",
    },
    {
        id: "scm",
        label: "供应链中台",
        labelEn: "Supply Chain",
        scopes: ["scm:*"],
        home: "/control-tower",
        blurb: "订单、采购、资金、质检、物流",
    },
    {
        id: "factory",
        label: "工厂运营",
        labelEn: "Factory",
        scopes: ["mfg:*"],
        home: "/factory",
        blurb: "产能、工单、生产进度、出货",
    },
    {
        id: "platform",
        label: "平台管理",
        labelEn: "Platform admin",
        scopes: ["platform:*"],
        home: "/admin/workflows",
        blurb: "文件、通知、审批流、搜索、i18n",
    },
    {
        id: "operator",
        label: "运维 / SRE",
        labelEn: "Operator",
        scopes: ["ops:*", "commerce:*", "rnd:*", "scm:*", "mfg:*", "platform:*"],
        home: "/admin/events",
        blurb: "事件运行时、死信重放、Outbox 健康",
    },
    {
        id: "superuser",
        label: "全权限演示",
        labelEn: "Full access",
        scopes: ["commerce:*", "rnd:*", "scm:*", "mfg:*", "platform:*", "ops:*"],
        home: "/workspace",
        blurb: "跨四系统演示（仅本地）",
    },
];
const STORAGE_KEY = "vnmade.session.v1";
export function presetById(id) {
    return PRESETS.find((p) => p.id === id) ?? PRESETS[0];
}
/** Base64 that survives UTF-8 (dev tokens are JSON with latin text only). */
function b64(s) {
    return btoa(unescape(encodeURIComponent(s)));
}
export function mintDevToken(input) {
    const tid = `org_${input.organizationId}`;
    const payload = {
        org: input.organizationId,
        sub: input.subject,
        scp: input.scopes,
        tid,
        cid: `web-${Date.now().toString(36)}`,
    };
    return `dev.${b64(JSON.stringify(payload))}`;
}
export function createSession(input) {
    const preset = presetById(input.preset);
    const session = {
        token: mintDevToken({
            organizationId: input.organizationId,
            subject: input.subject,
            scopes: preset.scopes,
        }),
        organizationId: input.organizationId,
        tenantId: `org_${input.organizationId}`,
        subject: input.subject,
        scopes: preset.scopes,
        preset: preset.id,
        issuedAt: new Date().toISOString(),
    };
    writeSession(session);
    return session;
}
export function readSession() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.token || !parsed.organizationId)
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
export function writeSession(s) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}
export function clearSession() {
    localStorage.removeItem(STORAGE_KEY);
}
/** Scope check that mirrors `hasScope` in platform/shared (`*` and `<domain>:*`). */
export function hasScope(scopes, required) {
    return scopes.some((s) => {
        if (s === "*")
            return true;
        if (s === required)
            return true;
        if (s.endsWith(":*"))
            return required.startsWith(s.slice(0, -1));
        if (s.endsWith("*"))
            return required.startsWith(s.slice(0, -1));
        return false;
    });
}
export const DEMO_ORG = "b0b0b0b0-b0b0-b0b0-b0b0-b0b0b0b0b0b0";
